import type { Epic } from 'Flux/index.js'
import { concat, concatMap, defer, filter, from, map, mergeMap, of, tap, withLatestFrom } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { orderSlice } from '../../../Slice/Order/OrderSlice.js'
import { authenticationExpiredOr } from '../../../../Framework/Epic/AuthenticationExpired.js'
import { addMessage } from '../../../Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../../Model/Message/IMessage.js'
import type { FileForProcessing, ISamplePool } from '../../../../Model/index.js'
import type { SamplePoolLabel } from '../../../../Generated/SamplePoolLabel.ts'
import type { ItemMove } from '../../../../View/Page/HomePage/Modals/SheetStructurePlan.ts'
import { rebaseOperations } from '../../../../View/Page/HomePage/Modals/SheetStructurePlan.ts'
import { mapToBatches } from '../../../../View/Page/HomePage/Modals/MapToBatches.js'

export const APPLY_SHEET_IMPORT_FAILURE_MESSAGE =
  'Applying the sample types and pools from the sheet failed part way. The order was reloaded to show what was saved; check it before importing again.'

const groupMoves = (moves: ItemMove[], poolIdOfRef: ReadonlyMap<string, string>) => {
  const groups = new Map<string, { sourcePoolId: string; targetPoolId: string; items: string[] }>()
  moves.forEach((move) => {
    const targetPoolId = poolIdOfRef.get(move.toPool) ?? move.toPool
    const key = `${move.fromPoolId}>${targetPoolId}`
    const group = groups.get(key) ?? { sourcePoolId: move.fromPoolId, targetPoolId, items: [] }
    group.items.push(move.itemId)
    groups.set(key, group)
  })
  return [...groups.values()]
}

/**
 * Applies a sheet import that changes the order's structure, strictly in order: create and name pools,
 * rename pools, move items, then the per-pool sample and metadata writes — rebased onto the pools and item
 * versions the earlier steps returned, which is why they cannot start together.
 */
export const ApplySheetImportEpic: Epic = (action$, state$, { [orderSlice.name]: order, OrderRepository }) =>
  action$.pipe(
    filter(order.applySheetImportRequest.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { orderId, plan, ops } = action.payload
      const user = state.authentication.user
      const poolIdOfRef = new Map<string, string>()
      const moved = new Map<string, FileForProcessing>()

      const creates$ = from(plan.creates).pipe(
        concatMap((create) =>
          OrderRepository.createSamplePool({ orderId }, user).pipe(
            concatMap((pool: ISamplePool) =>
              create.label && pool.version
                ? OrderRepository.updateSamplePoolLabel(
                    { orderId, poolId: pool.id, label: create.label, version: pool.version },
                    user
                  ).pipe(map((label: SamplePoolLabel) => ({ ...pool, label: label.label, version: label.version })))
                : of(pool)
            ),
            tap((pool) => poolIdOfRef.set(create.ref, pool.id)),
            map((pool) =>
              order.createSamplepoolSuccess({ ...pool, samples: pool.samples ?? [], negativeControls: pool.negativeControls ?? [] })
            )
          )
        )
      )

      const renames$ = from(plan.renames).pipe(
        concatMap((rename) =>
          OrderRepository.updateSamplePoolLabel(
            { orderId, poolId: rename.poolId, label: rename.to, version: rename.version ?? '' },
            user
          ).pipe(
            map((label) =>
              order.updateSamplePoolLabelSuccess({ orderId, poolId: rename.poolId, label: label.label, version: label.version })
            )
          )
        )
      )

      const moves$ = defer(() => from(groupMoves(plan.moves, poolIdOfRef))).pipe(
        concatMap((group) =>
          OrderRepository.movePoolItems({ orderId, ...group }, user).pipe(
            map((items) => items.filter((item): item is FileForProcessing => !!item)),
            tap((items) => items.forEach((item) => moved.set(item.id, item))),
            map((items) => order.movePoolItemsRequestSuccess({ orderId, ...group, items, itemIds: group.items }))
          )
        )
      )

      const writes$ = defer(() =>
        from(
          mapToBatches({ result: rebaseOperations(ops, plan, poolIdOfRef, moved), warnings: [], errors: [] }).map((batch) =>
            order.batchUpdateSamples(batch)
          )
        )
      )

      return concat(creates$, renames$, moves$, writes$, of(order.applySheetImportSuccess())).pipe(
        catchError((error) =>
          authenticationExpiredOr({
            error,
            failedRequest: action.type,
            failureAction: of(
              order.applySheetImportFailure({ error }),
              addMessage(errorMessage(APPLY_SHEET_IMPORT_FAILURE_MESSAGE)),
              order.getSampleMetaDatasRequest({ orderId }),
              order.getAllDatasetRequest({ orderId })
            )
          })
        )
      )
    })
  )
